/**
 * Hosted-login gate (G2 front door).
 *
 * Rendered only when Frontegg is enabled (see config/frontegg.ts) — its
 * hooks require the FronteggProvider above it. Unauthenticated visitors are
 * redirected to the hosted login box; children render only for a live
 * session.
 */

import { useEffect } from 'react';
import { InlineStack, Spinner } from '@shopify/polaris';
import { useAuth, useLoginWithRedirect } from '@frontegg/react';

export function AuthGuard({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth() as {
    isAuthenticated: boolean;
    isLoading?: boolean;
  };
  const loginWithRedirect = useLoginWithRedirect();

  const loading = isLoading ?? false;

  useEffect(() => {
    if (!loading && !isAuthenticated) {
      loginWithRedirect();
    }
  }, [loading, isAuthenticated, loginWithRedirect]);

  if (loading || !isAuthenticated) {
    return (
      <div style={{ paddingTop: '4rem' }}>
        <InlineStack align="center">
          <Spinner size="large" accessibilityLabel="Signing you in" />
        </InlineStack>
      </div>
    );
  }

  return <>{children}</>;
}
